import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import { Button, ProtectedRoute, Badge, Tabs, TabsList, TabsTrigger } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { NotificationBroadcastsTab } from '../app/components/notifications/notification-broadcasts-tab';

type AnnouncementsTab = 'announcements' | 'notifications';

type SearchParams = { tab: AnnouncementsTab };

export const Route = createFileRoute('/announcements')({
  component: AnnouncementsRoute,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    tab: search.tab === 'notifications' ? 'notifications' : 'announcements',
  }),
});

const statusVariants: Record<string, 'default' | 'outline' | 'secondary'> = {
  draft: 'outline',
  active: 'default',
  closed: 'secondary',
};

function formatScheduleDate(value: string | Date): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(value));
}

function AnnouncementsRoute() {
  const navigate = useNavigate({ from: '/announcements' });
  const { t } = useTranslation('admin');
  const { tab } = Route.useSearch();

  return (
    <ProtectedRoute requiredPermissions={['admin:all']}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{t('announcements.page.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('announcements.page.subtitle')}</p>
          </div>
          {tab === 'announcements' && (
            <Button variant="outline" onClick={() => navigate({ to: '/announcements/new' })}>
              {t('announcements.page.createButton')}
            </Button>
          )}
        </div>

        <Tabs
          value={tab}
          onValueChange={(value) =>
            navigate({ to: '/announcements', search: { tab: value as AnnouncementsTab }, replace: true })
          }
        >
          <TabsList>
            <TabsTrigger value="announcements">{t('announcements.tabs.announcements')}</TabsTrigger>
            <TabsTrigger value="notifications">{t('announcements.tabs.notifications')}</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'notifications' ? <NotificationBroadcastsTab /> : <AnnouncementsList />}
        <Outlet />
      </div>
    </ProtectedRoute>
  );
}

function AnnouncementsList() {
  const navigate = useNavigate({ from: '/announcements' });
  const { t } = useTranslation('admin');
  const { data: announcements, isLoading } = trpc.announcements.list.useQuery();

  return (
    <>
        {isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
        ) : !announcements || announcements.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            {t('announcements.list.empty')}
          </div>
        ) : (
          <div className="rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.title')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.mediaType')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.style')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.target')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.status')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.skippable')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.schedule')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.priority')}</th>
                  <th className="px-4 py-3 text-left font-medium">{t('announcements.list.impressions')}</th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr
                    key={announcement.announcementId}
                    className="cursor-pointer border-b transition hover:bg-muted/30"
                    onClick={() =>
                      navigate({
                        to: '/announcements/$announcementId',
                        params: { announcementId: announcement.announcementId },
                      })
                    }
                  >
                    <td className="px-4 py-3 font-medium">{announcement.title}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline">
                        {t(`announcements.list.mediaLabels.${announcement.mediaType}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {t(`announcements.list.styleLabels.${announcement.style ?? 'classic'}`)}
                    </td>
                    <td className="px-4 py-3">
                      {t(`announcements.list.targetLabels.${announcement.targetType}`)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusVariants[announcement.status] ?? 'outline'}>
                        {t(`announcements.list.statusLabels.${announcement.status}`)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {announcement.skippable
                        ? t('announcements.list.yes')
                        : t('announcements.list.no')}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {!announcement.startsAt && !announcement.endsAt ? (
                        <span>-</span>
                      ) : (
                        <span>
                          {announcement.startsAt ? formatScheduleDate(announcement.startsAt) : '...'}
                          {' - '}
                          {announcement.endsAt ? formatScheduleDate(announcement.endsAt) : '...'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">{announcement.priority}</td>
                    <td className="px-4 py-3 text-center">
                      {announcement.completedCount}/{announcement.impressionCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
}
